import { useSyncExternalStore } from "react";

/**
 * "Adicionar à tela inicial" no celular. O manifesto (public/favicon_io/
 * site.webmanifest) abre o app em /dashboard, que manda cada perfil para a
 * tela dele (o professor do Kids cai no /kids) — nunca na landing.
 *
 * - Android/Chrome: o navegador avisa que dá para instalar (`beforeinstallprompt`)
 *   logo que a página abre, às vezes antes de a parte interna do app carregar;
 *   por isso a escuta começa aqui, num módulo importado em main.tsx, e o
 *   evento fica guardado até a faixa pedir.
 * - iPhone: o Safari não deixa o site oferecer a instalação; a faixa mostra
 *   como fazer pelo menu Compartilhar.
 */

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferred: InstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Sem isto o Chrome mostra a barra dele na hora; a faixa decide quando
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    notify();
  });
}

/** Aberto como app (pelo ícone da tela inicial), e não no navegador */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iPhone ou iPad (o iPadOS se apresenta como Mac, com tela de toque) */
export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (ua.includes("Macintosh") && navigator.maxTouchPoints > 1)
  );
}

/** No iPhone, só o Safari tem o "Adicionar à Tela de Início" garantido */
export function isIosSafari(): boolean {
  return isIos() && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);
}

/** Celular ou tablet: tela de toque e estreita */
export function isMobileDevice(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(pointer: coarse)").matches &&
    Math.min(window.screen.width, window.screen.height) <= 1024
  );
}

/** O evento de instalação guardado (Android), ou null */
export function useInstallPrompt() {
  const event = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => deferred,
    () => null,
  );
  const wasInstalled = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => installed,
    () => false,
  );
  return {
    canPrompt: !!event,
    installed: wasInstalled,
    /** Abre a janela nativa; devolve true se a pessoa aceitou */
    async prompt(): Promise<boolean> {
      if (!deferred) return false;
      const current = deferred;
      deferred = null;
      notify();
      await current.prompt();
      const choice = await current.userChoice;
      return choice.outcome === "accepted";
    },
  };
}

/* ── a faixa fechada volta em 30 dias (decisão de 21/10/2026) ── */

const DISMISS_KEY = "ecclesiato:instalar-app:fechado-em";
const DISMISS_DAYS = 30;

export function wasDismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return !!at && Date.now() - at < DISMISS_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

export function rememberDismiss(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // sem armazenamento (aba privada): a faixa volta na próxima visita
  }
}
